import { Box, Button, DialogActions, FormControl, MenuItem, Typography } from '@mui/material'
import { useContext } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { useCreateDepartmentMutation, UserRole, useUpdateDepartmentMutation } from '../../../generated'
import { CaseContext } from '../../context'
import { DepartmentForm } from '../../interfaceTypes/forms'
import { CustomController } from '../common/CustomController'
import { CustomModal } from '../common/CustomModal'
import CustomMultiSelectChip from '../common/CustomMultiSelectChip'
import { Alert } from '../common/Alert'
import { DepartmentModalProps } from '../../interfaceTypes/props'
import UserDropdownWithForm from '../common/UserDropDownWithForm'
import { yupResolver } from '@hookform/resolvers/yup'
import { addDepartmentSchema } from '../../validationSchema'

const DepartmentModal = ({
  isOpen,
  handleClose,
  department,
  refetch,
  initialName,
  onCreated
}: DepartmentModalProps) => {
  const { allLawTypes } = useContext(CaseContext)

  const methods = useForm<DepartmentForm>({
    resolver: yupResolver(addDepartmentSchema),
    defaultValues: {
      id: department?.id || '',
      name: department?.name || initialName || '',
      description: department?.description || '',
      departmentHeadId: department?.departmentHeadId || '',
      lawTypeIds: department?.departmentToLawTypes?.map(({ lawType }) => lawType.id) || []
    }
  })

  const { control, reset, handleSubmit, clearErrors } = methods

  const closeModal = () => {
    reset()
    handleClose?.()
  }

  const [createDepartment] = useCreateDepartmentMutation({
    onError: error => {
      Alert.error(error.message)
      clearErrors()
    },
    onCompleted: data => {
      const { createDepartment } = data
      const { response, department: createdDepartment } = createDepartment
      const { message, status } = response ?? {}

      if (status) {
        console.log('success', data)
        Alert.success(message ?? '')
        refetch?.()
        createdDepartment && onCreated?.(createdDepartment)
        closeModal()
      }
    }
  })

  const [updateDepartment] = useUpdateDepartmentMutation({
    onError: error => {
      Alert.error(error.message)
      clearErrors()
    },
    onCompleted: data => {
      const { updateDepartment } = data
      const { response } = updateDepartment
      const { message, status } = response ?? {}

      if (status) {
        console.log('success', data)
        Alert.success(message ?? '')
        refetch?.()
        closeModal()
      }
    }
  })

  const onSubmit = async (data: DepartmentForm) => {
    const { id, departmentHeadId, ...rest } = data

    department?.id
      ? await updateDepartment({
          variables: {
            updateDepartmentInput: {
              departmentId: id,
              ...rest
            }
          }
        })
      : await createDepartment({
          variables: {
            createDepartmentInput: {
              ...rest,
              departmentHeadId: departmentHeadId || null
            }
          }
        })
  }

  return (
    <CustomModal
      isOpen={isOpen ?? false}
      handleClose={closeModal}
      title={`${department?.id ? 'Edit' : 'Add'} Department`}
    >
      <FormProvider {...methods}>
        <form
          onSubmit={event => {
            // The modal may be opened from inside another form (e.g. Add User); keep the submit from reaching it.
            event.stopPropagation()
            handleSubmit(onSubmit)(event)
          }}
          style={{ display: 'flex', flexDirection: 'column', gap: 2 }}
        >
          <CustomController controllerName="name" controllerLabel="Name" required />
          <FormControl variant="outlined" sx={{ marginTop: 1.5, marginBottom: 1 }} fullWidth>
            <Controller
              name="departmentHeadId"
              control={control}
              render={({ field, fieldState: { error = {} } }) => (
                <>
                  <UserDropdownWithForm
                    label={'Select Department Head'}
                    name="departmentHeadId"
                    sx={{
                      minWidth: 150
                    }}
                    roles={[UserRole.Attorney, UserRole.Paralegal, UserRole.Admin, UserRole.Investigator]}
                  />
                </>
              )}
            />
          </FormControl>

          <CustomMultiSelectChip
            name="lawTypeIds"
            label="Practice Areas"
            chipLabel={value => {
              const department = allLawTypes?.find(lawType => lawType?.id === value)
              return (
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="body1" fontSize={12}>
                    {department?.name}
                  </Typography>
                </Box>
              )
            }}
            children={allLawTypes?.map(item => {
              const { id, name } = item ?? {}
              return (
                <MenuItem value={id} key={id}>
                  <Box display={'flex'} alignItems={'center'} gap={1}>
                    <Typography variant="body1" fontSize={14}>
                      {name}
                    </Typography>
                  </Box>
                </MenuItem>
              )
            })}
            getLabel={value => {
              const lawType = allLawTypes?.find(law => law?.id === value)
              return lawType?.name ?? ''
            }}
          />
          <CustomController controllerName="description" controllerLabel="Description" rowsLength={4} isMultiLine />
          <DialogActions>
            <Button variant="text" onClick={closeModal} sx={{ color: 'gray' }}>
              Cancel
            </Button>

            <Button type="submit" variant="contained">
              {department?.id ? 'Save' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </CustomModal>
  )
}

export default DepartmentModal
